import { useState } from "react";
import { CheckCircle2, XCircle, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { IMPORT_ENTITIES, type ImportEntityType, type ImportResult } from "@/hooks/useQBImport";
import { ScrollArea } from "@/components/ui/scroll-area";

interface EntityStatus {
  entity: ImportEntityType;
  status: "pending" | "running" | "done" | "error";
  result?: ImportResult;
  error?: string;
}

interface Props {
  es: EntityStatus;
  companyId: string;
}

const ENTITY_TABLE_MAP: Partial<Record<ImportEntityType, { table: string; nameCol: string; qbCol: string }>> = {
  vendors: { table: "vendors", nameCol: "name", qbCol: "qb_vendor_id" },
  customers: { table: "customers", nameCol: "name", qbCol: "qb_customer_id" },
  accounts: { table: "chart_of_accounts", nameCol: "name", qbCol: "qb_account_id" },
};

export function ImportResultRow({ es, companyId }: Props) {
  const entity = IMPORT_ENTITIES.find((e) => e.key === es.entity)!;
  const count = es.result?.[es.entity] ?? 0;
  const bills = es.result?.bills ?? 0;
  const skipped = es.result?.skipped_duplicates ?? 0;

  const [expanded, setExpanded] = useState(false);
  const [records, setRecords] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const mapping = ENTITY_TABLE_MAP[es.entity];
  const canExpand = es.status === "done" && count > 0 && !!mapping;

  const fetchRecords = async () => {
    if (records || !mapping) return;
    setLoading(true);
    try {
      const { data, count: total } = await supabase
        .from(mapping.table as any)
        .select(mapping.nameCol, { count: "exact" })
        .eq("company_id", companyId)
        .not(mapping.qbCol, "is", null)
        .order(mapping.nameCol, { ascending: true })
        .limit(100);

      const names = (data || []).map((r: any) => r[mapping.nameCol] as string);
      setRecords(names);
      setTotalCount(total ?? names.length);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (!canExpand) return;
    const next = !expanded;
    setExpanded(next);
    if (next) fetchRecords();
  };

  return (
    <div className="rounded-md border overflow-hidden">
      <div
        className={`flex items-center gap-3 p-2 ${canExpand ? "cursor-pointer hover:bg-accent/50 transition-colors" : ""}`}
        onClick={handleClick}
      >
        {es.status === "done" ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        ) : (
          <XCircle className="h-4 w-4 text-destructive" />
        )}
        <span className="text-lg">{entity.icon}</span>
        <span className="text-sm font-medium flex-1">{entity.label}</span>
        {es.status === "done" && (
          <div className="flex items-center gap-2 text-right">
            <span className="text-sm font-medium">{count} imported</span>
            {bills > 0 && <span className="text-xs text-muted-foreground">{bills} bills</span>}
            {skipped > 0 && <span className="text-xs text-muted-foreground">{skipped} skipped</span>}
            {canExpand && (
              expanded
                ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                : <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        )}
        {es.status === "error" && (
          <span className="text-xs text-destructive">{es.error}</span>
        )}
      </div>

      {expanded && (
        <div className="border-t bg-muted/20 px-3 py-2">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading records…
            </div>
          )}
          {records && records.length > 0 && (
            <>
              <ScrollArea className="max-h-48">
                <ul className="space-y-0.5">
                  {records.map((name, i) => (
                    <li key={i} className="text-xs text-foreground py-0.5 px-1 rounded hover:bg-accent/30">
                      {name}
                    </li>
                  ))}
                </ul>
              </ScrollArea>
              {totalCount > 100 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Showing 100 of {totalCount} records
                </p>
              )}
            </>
          )}
          {records && records.length === 0 && (
            <p className="text-xs text-muted-foreground py-1">No records found.</p>
          )}
        </div>
      )}
    </div>
  );
}
